/**
 * The small stuff that makes a place feel lived in: crates and barrels, pots,
 * stalls and carts, fences, bikes, strings of lights and laundry, plants at
 * the water's edge, and the flat decals baked into the floor (leaves,
 * petals, puddles, cracks, road markings).
 */
import { footprint, type Prop } from '../../../shared/world.ts'
import { mix, shade } from './color.ts'
import { blobShadow, box, castShadow, cyl, iso, poly, rnd, type Pt } from './iso.ts'
import { onFloor } from './props.ts'

type Ctx = CanvasRenderingContext2D

const faceY = (y: number, x0: number, x1: number, z0: number, z1: number): Pt[] => [iso(x0, y, z0), iso(x1, y, z0), iso(x1, y, z1), iso(x0, y, z1)]
const BULBS = ['#ffd166', '#ff8fab', '#7ee0c3', '#b69cff', '#ff9e6b']

/** Text painted on the +y face of something, skewed to sit on it. */
function sign(ctx: Ctx, text: string, c: Pt, size: number, fill: string, bg?: string): void {
  ctx.save()
  ctx.translate(c.x, c.y)
  ctx.transform(1, 0.5, 0, 1, 0, 0)
  ctx.font = `800 ${size}px Nunito, ui-rounded, system-ui, sans-serif`
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  if (bg) { const w = ctx.measureText(text).width + 8; ctx.fillStyle = bg; ctx.beginPath(); ctx.roundRect(-w / 2, -size * 0.75, w, size * 1.5, 3); ctx.fill() }
  ctx.fillStyle = fill
  ctx.fillText(text, 0, 0)
  ctx.restore()
}

export function drawClutter(ctx: Ctx, p: Prop, t: number, day: number, seed: number): boolean {
  const { w, d } = footprint(p)
  const x = p.x, y = p.y
  const cx = x + w / 2, cy = y + d / 2
  const night = 1 - day
  switch (p.kind) {
    case 'crate': {
      castShadow(ctx, x + 0.18, y + 0.18, 0.64, 0.64, 0.6, 0.14)
      const c = seed % 2 ? '#c9945c' : '#b98559'
      box(ctx, x + 0.18, y + 0.18, 0, 0.64, 0.64, 0.6, c)
      ctx.strokeStyle = 'rgba(80,50,20,0.35)'; ctx.lineWidth = 1
      for (const f of [faceY(y + 0.82, x + 0.18, x + 0.82, 0, 0.6)]) { ctx.beginPath(); ctx.moveTo(f[0]!.x, f[0]!.y); ctx.lineTo(f[2]!.x, f[2]!.y); ctx.moveTo(f[1]!.x, f[1]!.y); ctx.lineTo(f[3]!.x, f[3]!.y); ctx.stroke() }
      return true
    }
    case 'barrel': {
      blobShadow(ctx, cx, cy, 0.34, 0.2)
      cyl(ctx, cx, cy, 0, 0.3, 0.75, '#a0683c', { top: '#7a4f2e' })
      ctx.strokeStyle = '#4a4466'; ctx.lineWidth = 1.6
      for (const z of [0.15, 0.6]) { const q = iso(cx, cy, z); ctx.beginPath(); ctx.ellipse(q.x, q.y, 0.3 * 32, 0.3 * 16, 0, 0, Math.PI); ctx.stroke() }
      return true
    }
    case 'pot': {
      const v = p.variant ?? 'flower'
      blobShadow(ctx, cx, cy, 0.3, 0.2)
      const potC = v === 'bonsai' ? '#3d6b8f' : v === 'tree' ? '#9aa0b0' : ['#d98c6b', '#c9b7a0', '#8ea8b8'][seed % 3]!
      if (v === 'tree') {
        box(ctx, x + 0.15, y + 0.15, 0, 0.7, 0.7, 0.45, potC)
        poly(ctx, [iso(x + 0.2, y + 0.2, 0.45), iso(x + 0.8, y + 0.2, 0.45), iso(x + 0.8, y + 0.8, 0.45), iso(x + 0.2, y + 0.8, 0.45)], '#5a3f2c')
        cyl(ctx, cx, cy, 0.45, 0.06, 0.9, '#8a6a4a')
        const top = iso(cx, cy, 1.6)
        for (const [dx, dy, r, c] of [[-8, 4, 11, '#5cc98a'], [8, 3, 10, '#4fb97d'], [0, -6, 13, '#6ad89a']] as const) {
          ctx.fillStyle = c; ctx.beginPath(); ctx.arc(top.x + dx, top.y + dy, r, 0, Math.PI * 2); ctx.fill()
        }
        return true
      }
      cyl(ctx, cx, cy, 0, 0.2, 0.3, potC, { top: '#5a3f2c' })
      const b = iso(cx, cy, 0.32)
      if (v === 'bonsai') {
        ctx.strokeStyle = '#6b4a3a'; ctx.lineWidth = 2.5; ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.quadraticCurveTo(b.x - 6, b.y - 8, b.x + 2, b.y - 14); ctx.stroke()
        for (const [dx, dy, r] of [[-8, -12, 7], [6, -16, 8], [-1, -20, 6]] as const) { ctx.fillStyle = '#4f9a5a'; ctx.beginPath(); ctx.ellipse(b.x + dx, b.y + dy, r, r * 0.6, 0, 0, Math.PI * 2); ctx.fill() }
      } else {
        const fl = ['#ff8fab', '#ffd166', '#b69cff', '#ff6b6b'][seed % 4]!
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2
          ctx.fillStyle = i % 2 ? '#5cc98a' : fl
          ctx.beginPath(); ctx.arc(b.x + Math.cos(a) * 6, b.y - 5 + Math.sin(a) * 3, i % 2 ? 3.5 : 3, 0, Math.PI * 2); ctx.fill()
        }
      }
      return true
    }
    case 'mailbox': {
      blobShadow(ctx, cx, cy, 0.22, 0.18)
      box(ctx, cx - 0.03, cy - 0.03, 0, 0.06, 0.06, 0.9, '#6b4a34', { edge: false })
      box(ctx, cx - 0.12, cy - 0.22, 0.9, 0.24, 0.44, 0.24, '#e0443a')
      const f = iso(cx + 0.12, cy - 0.1, 1.05); ctx.fillStyle = '#ffd166'; ctx.fillRect(f.x, f.y - 6, 2, 7)
      return true
    }
    case 'bin': {
      blobShadow(ctx, cx, cy, 0.25, 0.18)
      cyl(ctx, cx, cy, 0, 0.2, 0.6, '#5a7d6a', { top: '#3f5a4a' })
      cyl(ctx, cx, cy, 0.6, 0.22, 0.06, '#4a6a5a')
      return true
    }
    case 'bicycle': {
      const col = p.color ?? '#5aa9c9'
      const a = iso(x + 0.15, cy, 0.22), b = iso(x + 0.85, cy, 0.22)
      ctx.strokeStyle = '#2d2a3e'; ctx.lineWidth = 2
      for (const q of [a, b]) { ctx.beginPath(); ctx.ellipse(q.x, q.y, 8, 7, 0, 0, Math.PI * 2); ctx.stroke() }
      const seat = iso(x + 0.4, cy, 0.6), bar = iso(x + 0.78, cy, 0.68)
      ctx.strokeStyle = col; ctx.lineWidth = 2.5
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(seat.x, seat.y); ctx.lineTo(bar.x, bar.y); ctx.lineTo(b.x, b.y); ctx.moveTo(a.x, a.y); ctx.lineTo((a.x + b.x) / 2, (a.y + b.y) / 2); ctx.lineTo(seat.x, seat.y); ctx.stroke()
      ctx.fillStyle = '#2d2a3e'; ctx.fillRect(seat.x - 4, seat.y - 2, 8, 2.5)
      return true
    }
    case 'fence': {
      const along = !p.rot
      const wood = '#e8dcc8'
      if (along) {
        for (const u of [0.1, 0.9]) box(ctx, x + u - 0.05, cy - 0.05, 0, 0.1, 0.1, 0.62, wood, { edge: false })
        for (const z of [0.2, 0.45]) box(ctx, x, cy - 0.03, z, 1, 0.06, 0.08, shade(wood, -0.04), { edge: false })
      } else {
        for (const u of [0.1, 0.9]) box(ctx, cx - 0.05, y + u - 0.05, 0, 0.1, 0.1, 0.62, wood, { edge: false })
        for (const z of [0.2, 0.45]) box(ctx, cx - 0.03, y, z, 0.06, 1, 0.08, shade(wood, -0.08), { edge: false })
      }
      return true
    }
    case 'stall': {
      const col = p.color ?? '#ff8fab'
      castShadow(ctx, x, y + 0.1, w, d - 0.2, 1.6, 0.14)
      box(ctx, x + 0.1, y + 0.2, 0, w - 0.2, d - 0.4, 0.8, '#c9945c')
      // Goods on the counter.
      for (let i = 0; i < 6; i++) {
        const q = iso(x + 0.3 + i * ((w - 0.6) / 5), y + 0.5 + (i % 2) * 0.12, 0.82)
        ctx.fillStyle = [col, '#ffd166', '#7cf29c', '#ff6b6b', '#ffffff', '#b69cff'][(i + seed) % 6]!
        ctx.beginPath(); ctx.arc(q.x, q.y - 2.5, 3.6, 0, Math.PI * 2); ctx.fill()
      }
      for (const u of [x + 0.15, x + w - 0.2]) box(ctx, u, y + 0.25, 0.8, 0.06, 0.06, 1.0, '#6b4a34', { edge: false })
      // Striped canopy.
      const n = 6
      for (let i = 0; i < n; i++) {
        const a = x + (i / n) * w, b = x + ((i + 1) / n) * w
        poly(ctx, [iso(a, y + 0.1, 1.85), iso(b, y + 0.1, 1.85), iso(b, y + d - 0.05, 1.55), iso(a, y + d - 0.05, 1.55)], i % 2 ? '#ffffff' : col)
      }
      if (p.text) sign(ctx, p.text, iso(cx, y + 0.8, 0.45), 8, '#4a3a2c', '#fff6e8')
      return true
    }
    case 'cart': {
      const col = p.color ?? '#ff8fab'
      castShadow(ctx, x, y + 0.15, w, d - 0.3, 1.2, 0.14)
      for (const u of [x + 0.35, x + w - 0.35]) { const q = iso(u, y + d - 0.15, 0.2); ctx.fillStyle = '#3a3552'; ctx.beginPath(); ctx.ellipse(q.x, q.y, 6, 6, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = '#9aa0b0'; ctx.beginPath(); ctx.arc(q.x, q.y, 2, 0, Math.PI * 2); ctx.fill() }
      box(ctx, x + 0.1, y + 0.2, 0.25, w - 0.2, d - 0.4, 0.65, col)
      box(ctx, x + 0.05, y + 0.15, 0.9, w - 0.1, d - 0.3, 0.06, '#ffffff')
      for (const u of [x + 0.15, x + w - 0.2]) box(ctx, u, y + 0.3, 0.96, 0.05, 0.05, 0.75, '#ffffff', { edge: false })
      poly(ctx, [iso(x - 0.05, y + 0.1, 1.85), iso(x + w + 0.05, y + 0.1, 1.85), iso(x + w + 0.05, y + d - 0.1, 1.65), iso(x - 0.05, y + d - 0.1, 1.65)], shade(col, 0.15))
      if (p.text) sign(ctx, p.text, iso(cx, y + d - 0.2, 0.6), 8, '#ffffff')
      return true
    }
    case 'well': {
      blobShadow(ctx, cx, cy, 0.45, 0.2)
      cyl(ctx, cx, cy, 0, 0.38, 0.5, '#b4ada3', { top: '#3f6f8f' })
      for (const u of [-0.32, 0.32]) box(ctx, cx + u - 0.04, cy - 0.04, 0.5, 0.08, 0.08, 0.9, '#6b4a34', { edge: false })
      const a = iso(cx - 0.45, cy, 1.4), b = iso(cx + 0.45, cy, 1.4), top = iso(cx, cy, 1.8)
      ctx.fillStyle = '#b0503c'; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(top.x, top.y); ctx.lineTo(b.x, b.y); ctx.lineTo(b.x, b.y + 4); ctx.lineTo(a.x, a.y + 4); ctx.closePath(); ctx.fill()
      const q = iso(cx, cy, 0.9); ctx.strokeStyle = '#6b4a34'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(q.x, q.y - 10); ctx.lineTo(q.x, q.y + 4); ctx.stroke()
      ctx.fillStyle = '#8a6a4a'; ctx.fillRect(q.x - 3, q.y + 4, 6, 5)
      return true
    }
    case 'birdbath': {
      blobShadow(ctx, cx, cy, 0.3, 0.18)
      cyl(ctx, cx, cy, 0, 0.16, 0.1, '#d8d0c4')
      cyl(ctx, cx, cy, 0.1, 0.06, 0.55, '#d8d0c4')
      cyl(ctx, cx, cy, 0.65, 0.32, 0.1, '#e2dbd0', { top: '#8fd6ef' })
      if (Math.sin(t * 0.7 + seed) > 0.2) {
        const b = iso(cx + 0.18, cy, 0.78); const hop = Math.abs(Math.sin(t * 6)) * 2
        ctx.fillStyle = '#7f849c'; ctx.beginPath(); ctx.ellipse(b.x, b.y - 3 - hop, 4, 3, 0, 0, Math.PI * 2); ctx.fill()
        ctx.beginPath(); ctx.arc(b.x + 3, b.y - 6 - hop, 2.2, 0, Math.PI * 2); ctx.fill()
      }
      return true
    }
    case 'bollard': {
      cyl(ctx, cx, cy, 0, 0.12, 0.45, '#4a4466', { top: '#5a5476' })
      return true
    }
    case 'aboard': {
      blobShadow(ctx, cx, cy, 0.25, 0.16)
      poly(ctx, [iso(cx - 0.3, cy + 0.15, 0), iso(cx + 0.3, cy + 0.15, 0), iso(cx + 0.3, cy, 0.9), iso(cx - 0.3, cy, 0.9)], '#3a3552')
      poly(ctx, [iso(cx - 0.25, cy + 0.12, 0.12), iso(cx + 0.25, cy + 0.12, 0.12), iso(cx + 0.25, cy + 0.02, 0.8), iso(cx - 0.25, cy + 0.02, 0.8)], '#2f3a36')
      if (p.text) sign(ctx, p.text, iso(cx, cy + 0.08, 0.5), 7, '#ffffff')
      return true
    }
    case 'festoon':
    case 'laundry': {
      // Two posts at the ends of the long side and a sagging line between them.
      const alongY = d > w
      const [ax, ay, bx, by] = alongY ? [cx, y + 0.3, cx, y + d - 0.3] : [x + 0.3, cy, x + w - 0.3, cy]
      const H = p.kind === 'laundry' ? 1.9 : 2.5
      const wood = p.kind === 'laundry' ? '#6b4a34' : '#4a4466'
      cyl(ctx, ax, ay, 0, 0.05, H, wood); cyl(ctx, bx, by, 0, 0.05, H, wood)
      const a = iso(ax, ay, H), b = iso(bx, by, H)
      const sag = p.kind === 'laundry' ? 10 : 16
      const at = (k: number) => ({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k + Math.sin(k * Math.PI) * sag })
      ctx.strokeStyle = 'rgba(40,30,50,0.7)'; ctx.lineWidth = 1
      ctx.beginPath(); for (let k = 0; k <= 1.001; k += 0.05) { const q = at(k); if (k === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y) } ctx.stroke()
      if (p.kind === 'laundry') {
        const cols = ['#ffffff', '#6ec6ff', '#ff8fab', '#ffd166', '#7cf29c']
        for (let i = 1; i < 6; i++) {
          const k = i / 6, q = at(k)
          const sway = Math.sin(t * 2 + i) * 1.5
          ctx.fillStyle = cols[(i + seed) % cols.length]!
          ctx.beginPath(); ctx.moveTo(q.x - 5, q.y); ctx.lineTo(q.x + 5, q.y); ctx.lineTo(q.x + 4 + sway, q.y + 12); ctx.lineTo(q.x - 4 + sway, q.y + 12); ctx.closePath(); ctx.fill()
        }
      } else {
        const lanterns = p.variant === 'lanterns'
        const n = lanterns ? 5 : 9
        for (let i = 1; i < n; i++) {
          const q = at(i / n)
          if (lanterns) {
            ctx.fillStyle = mix('#c0392b', '#ff6b4a', night); ctx.beginPath(); ctx.ellipse(q.x, q.y + 6, 4.5, 5.5, 0, 0, Math.PI * 2); ctx.fill()
            ctx.fillStyle = '#ffd166'; ctx.fillRect(q.x - 2, q.y, 4, 1.4)
          } else {
            const c = BULBS[(i + seed) % BULBS.length]!
            ctx.fillStyle = night > 0.3 ? c : mix('#ffffff', c, 0.5)
            ctx.beginPath(); ctx.arc(q.x, q.y + 3, 2.4, 0, Math.PI * 2); ctx.fill()
          }
        }
      }
      return true
    }
    case 'reeds': {
      const b = iso(cx, cy, 0)
      for (let i = 0; i < 7; i++) {
        const dx = (rnd(i, seed) - 0.5) * 22, h = 16 + rnd(seed, i) * 14
        const sway = Math.sin(t * 1.3 + i) * 1.5
        ctx.strokeStyle = i % 2 ? '#6fa85a' : '#8fc06a'; ctx.lineWidth = 2
        ctx.beginPath(); ctx.moveTo(b.x + dx, b.y); ctx.quadraticCurveTo(b.x + dx + sway, b.y - h * 0.6, b.x + dx + sway * 2, b.y - h); ctx.stroke()
        if (i % 3 === 0) { ctx.fillStyle = '#7a4f2e'; ctx.beginPath(); ctx.ellipse(b.x + dx + sway * 2, b.y - h - 3, 2, 5, 0, 0, Math.PI * 2); ctx.fill() }
      }
      return true
    }
    default:
      return false
  }
}

/** Flat things: forest-floor plants and decals. Drawn into the cached floor. */
export function drawFlatClutter(ctx: Ctx, p: Prop): boolean {
  const x = p.x, y = p.y
  const s = Math.floor(x * 13 + y * 7)
  if (p.kind === 'mushroom') {
    for (let i = 0; i < 3; i++) {
      const q = iso(x + 0.3 + rnd(i, s) * 0.4, y + 0.3 + rnd(s, i) * 0.4)
      ctx.fillStyle = '#f2ece2'; ctx.fillRect(q.x - 1, q.y - 5, 2, 5)
      ctx.fillStyle = i % 2 ? '#e8484f' : '#c98d52'; ctx.beginPath(); ctx.ellipse(q.x, q.y - 5, 4.5, 3, 0, Math.PI, 0); ctx.fill()
      if (i % 2) { ctx.fillStyle = '#ffffff'; ctx.fillRect(q.x - 2, q.y - 7, 1.4, 1.4) }
    }
    return true
  }
  if (p.kind === 'fern') {
    const b = iso(x + 0.5, y + 0.5)
    ctx.strokeStyle = '#4f9a4a'; ctx.lineWidth = 2
    for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + (i - 2) * 0.5; ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.quadraticCurveTo(b.x + Math.cos(a) * 8, b.y + Math.sin(a) * 8, b.x + Math.cos(a) * 14, b.y + Math.sin(a) * 6 + 2); ctx.stroke() }
    return true
  }
  if (p.kind !== 'decal') return false
  switch (p.variant) {
    case 'leaves':
      for (let i = 0; i < 6; i++) {
        const q = iso(x + rnd(i, s, 1), y + rnd(i, s, 2))
        ctx.fillStyle = ['#d98c3c', '#c9a23a', '#b0503c', '#8a6a3a'][i % 4]!
        ctx.beginPath(); ctx.ellipse(q.x, q.y, 3, 1.6, rnd(i, s, 3) * 3, 0, Math.PI * 2); ctx.fill()
      }
      return true
    case 'petals':
      for (let i = 0; i < 7; i++) {
        const q = iso(x + rnd(i, s, 1), y + rnd(i, s, 2))
        ctx.fillStyle = i % 3 ? '#ffc6d9' : '#ffe0ec'
        ctx.beginPath(); ctx.ellipse(q.x, q.y, 2.4, 1.4, rnd(i, s, 3) * 3, 0, Math.PI * 2); ctx.fill()
      }
      return true
    case 'clover':
      for (let i = 0; i < 4; i++) {
        const q = iso(x + rnd(i, s, 1), y + rnd(i, s, 2))
        ctx.fillStyle = i === 0 ? '#ffffff' : '#7cc06a'
        for (let k = 0; k < 3; k++) { ctx.beginPath(); ctx.arc(q.x + Math.cos(k * 2.1) * 1.8, q.y + Math.sin(k * 2.1) * 1.1, 1.6, 0, Math.PI * 2); ctx.fill() }
      }
      return true
    case 'moss':
      onFloor(ctx, 0.003, () => { ctx.fillStyle = 'rgba(110,150,80,0.28)'; ctx.beginPath(); ctx.ellipse(x + 0.5, y + 0.5, 0.42, 0.3, rnd(s, 1) * 3, 0, Math.PI * 2); ctx.fill() })
      return true
    case 'puddle':
      onFloor(ctx, 0.003, () => {
        ctx.fillStyle = 'rgba(120,170,210,0.45)'; ctx.beginPath(); ctx.ellipse(x + 0.5, y + 0.5, 0.36, 0.24, rnd(s, 2) * 3, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.beginPath(); ctx.ellipse(x + 0.42, y + 0.44, 0.12, 0.05, 0.3, 0, Math.PI * 2); ctx.fill()
      })
      return true
    case 'crack': {
      ctx.strokeStyle = 'rgba(60,50,40,0.28)'; ctx.lineWidth = 1
      const a = iso(x + 0.2, y + 0.3), b = iso(x + 0.5, y + 0.55), c = iso(x + 0.8, y + 0.45), e = iso(x + 0.55, y + 0.85)
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.lineTo(c.x, c.y); ctx.moveTo(b.x, b.y); ctx.lineTo(e.x, e.y); ctx.stroke()
      return true
    }
    case 'manhole':
      onFloor(ctx, 0.003, () => {
        ctx.fillStyle = '#44485a'; ctx.beginPath(); ctx.arc(x + 0.5, y + 0.5, 0.3, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 0.03
        for (const r of [0.12, 0.22]) { ctx.beginPath(); ctx.arc(x + 0.5, y + 0.5, r, 0, Math.PI * 2); ctx.stroke() }
      })
      return true
    case 'roadline':
      onFloor(ctx, 0.004, () => { ctx.fillStyle = 'rgba(255,214,102,0.85)'; ctx.fillRect(x + 0.1, y + 0.45, 0.8, 0.1) })
      return true
    case 'roadline-y':
      onFloor(ctx, 0.004, () => { ctx.fillStyle = 'rgba(255,214,102,0.85)'; ctx.fillRect(x + 0.45, y + 0.1, 0.1, 0.8) })
      return true
    case 'shells':
      for (let i = 0; i < 3; i++) {
        const q = iso(x + rnd(i, s, 1), y + rnd(i, s, 2))
        ctx.fillStyle = ['#ffd8c2', '#fff2e0', '#f6c6d0'][i]!; ctx.beginPath(); ctx.arc(q.x, q.y, 2.6, Math.PI, 0); ctx.fill()
      }
      return true
    case 'seaweed': {
      const q = iso(x + 0.5, y + 0.5)
      ctx.strokeStyle = 'rgba(70,110,60,0.7)'; ctx.lineWidth = 2
      ctx.beginPath(); ctx.moveTo(q.x - 8, q.y); ctx.quadraticCurveTo(q.x - 2, q.y - 3, q.x + 2, q.y + 1); ctx.quadraticCurveTo(q.x + 6, q.y + 3, q.x + 9, q.y - 1); ctx.stroke()
      return true
    }
    default:
      return true
  }
}
